import { ZHE_NAMES } from "@xinghai/shared";
import type { Domain, MarkType, Zhe } from "@xinghai/shared";
import { Button, Field, Icon, Input, SegmentedControl, Select, Slider } from "@xinghai/ui";
import type { CharDimKey } from "../labels";
import {
  CHAR_DIMS,
  DIMENSION_LABEL,
  FINAL_OPTIONS,
  INITIAL_OPTIONS,
  MARK_LABEL,
  MARK_ORDER,
  TONE_OPTIONS,
} from "../labels";
import { applyMarkDefaults, applyOmission } from "../draft";
import type { CharDraft } from "../draft";
import type { AnnotateMaterialChar } from "../types";

export interface CharRowsProps {
  chars: readonly AnnotateMaterialChar[];
  /** Display pinyin per char, already tone-marked (or stripped for singing). */
  pinyin: readonly string[];
  domain: Domain;
  values: readonly CharDraft[];
  onChange: (index: number, next: CharDraft) => void;
  /** Which row is expanded, or `null`. */
  expanded: number | null;
  onExpandedChange: (index: number | null) => void;
  /** Row the audio playhead is parked on. */
  active: number;
  onActivate: (index: number) => void;
  disabled?: boolean;
}

/**
 * docs/UX.md §2.3 — 多字工作台: one compact row per character
 * (字 / 拼音 / ✓正确 / 有问题▸). Untouched rows count as 正确; the detail form
 * only appears for the row the expert opened.
 */
export function CharRows({
  chars,
  pinyin,
  domain,
  values,
  onChange,
  expanded,
  onExpandedChange,
  active,
  onActivate,
  disabled = false,
}: CharRowsProps) {
  const singing = domain === "singing";
  const dims = CHAR_DIMS[domain];

  const markCorrect = (i: number, value: CharDraft) => {
    onChange(i, applyMarkDefaults(value, domain, "correct"));
    if (expanded === i) onExpandedChange(null);
  };

  const openProblem = (i: number, value: CharDraft) => {
    onExpandedChange(expanded === i ? null : i);
    onActivate(i);
    if (value.mark_type === null || value.mark_type === "correct") {
      onChange(i, applyMarkDefaults(value, domain, "substitution"));
    }
  };

  return (
    <ul className="xz-charrows" aria-label="逐字评分">
      <li className="xz-charrows__head" aria-hidden="true">
        <span>字</span>
        <span>拼音</span>
        <span>{singing ? "字头 / 字腹 / 字尾 / 辙位" : "判断"}</span>
      </li>
      {chars.map((ch, i) => {
        const value = values[i];
        if (!value) return null;
        const isOpen = expanded === i;
        const problem = value.mark_type !== null && value.mark_type !== "correct";
        return (
          <li
            key={`${ch.char}-${i}`}
            className={["xz-charrows__item", i === active && "is-active", problem && "has-problem"]
              .filter(Boolean)
              .join(" ")}
          >
            <div className="xz-charrows__row">
              <button
                type="button"
                className="xz-charrows__char"
                aria-label={`第 ${i + 1} 字 ${ch.char}，跳到这里播放`}
                onClick={() => onActivate(i)}
              >
                {ch.char}
              </button>
              <span className="xz-charrows__pinyin">{pinyin[i] ?? ch.pinyin_locked}</span>
              <span className="xz-charrows__actions">
                <Button
                  variant={problem ? "ghost" : "primary"}
                  size="sm"
                  disabled={disabled}
                  aria-pressed={!problem}
                  onClick={() => markCorrect(i, value)}
                >
                  <Icon id="ui-check" size={16} /> 正确
                </Button>
                <Button
                  variant={problem ? "secondary" : "ghost"}
                  size="sm"
                  disabled={disabled}
                  aria-expanded={isOpen}
                  onClick={() => openProblem(i, value)}
                >
                  有问题
                  <Icon id={isOpen ? "ui-chevron-down" : "ui-chevron-right"} size={16} />
                </Button>
                {problem && value.mark_type && (
                  <span className="xz-charrows__mark">{MARK_LABEL[value.mark_type]}</span>
                )}
              </span>
            </div>

            {isOpen && (
              <div className="xz-charrows__detail">
                <SegmentedControl<MarkType>
                  size="sm"
                  label={`第 ${i + 1} 字标记类型`}
                  options={MARK_ORDER.map((m, k) => ({
                    value: m,
                    label: MARK_LABEL[m],
                    title: `${MARK_LABEL[m]}（快捷键 ${k + 1}）`,
                  }))}
                  value={value.mark_type}
                  disabled={disabled}
                  onChange={(m) => onChange(i, applyMarkDefaults(value, domain, m))}
                />

                {value.mark_type === "deletion" && (
                  <SegmentedControl<CharDimKey>
                    size="sm"
                    label="省略的是哪一部分"
                    options={dims.map((d) => ({ value: d, label: DIMENSION_LABEL[d] ?? d }))}
                    value={
                      dims.find(
                        (d) => (value as unknown as Record<CharDimKey, number | null>)[d] === 0,
                      ) ?? null
                    }
                    disabled={disabled}
                    onChange={(d) => onChange(i, applyOmission(value, d))}
                  />
                )}

                {value.mark_type === "substitution" && (
                  <Field label="实现音素">
                    <Select
                      value={value.realized ?? ""}
                      disabled={disabled}
                      onChange={(e) =>
                        onChange(i, { ...value, realized: e.target.value || null, touched: true })
                      }
                    >
                      <option value="">未选择</option>
                      <optgroup label="声母">
                        {INITIAL_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="韵母">
                        {FINAL_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </optgroup>
                    </Select>
                  </Field>
                )}

                {!singing && value.tone !== null && value.tone < 1 && (
                  <Field label="实现调类">
                    <SegmentedControl<number>
                      size="sm"
                      label={`第 ${i + 1} 字实现调类`}
                      options={TONE_OPTIONS.map((t) => ({ value: t.value, label: t.label }))}
                      value={value.realized_tone}
                      disabled={disabled}
                      onChange={(t) => onChange(i, { ...value, realized_tone: t, touched: true })}
                    />
                  </Field>
                )}

                {singing && (
                  <Field label="辙位">
                    <Select
                      value={value.zhe_realized ?? ""}
                      disabled={disabled}
                      onChange={(e) =>
                        onChange(i, {
                          ...value,
                          zhe_realized: (e.target.value || null) as Zhe | null,
                          touched: true,
                        })
                      }
                    >
                      <option value="">未选择</option>
                      {ZHE_NAMES.map((z) => (
                        <option key={z} value={z}>
                          {z}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}

                <div className="xz-charjudge__dims">
                  {dims.map((dim) => (
                    <label className="xz-charjudge__dim" key={dim}>
                      <span className="xz-charjudge__dimname">{DIMENSION_LABEL[dim]}</span>
                      <Slider
                        value={(value as unknown as Record<CharDimKey, number | null>)[dim]}
                        step={0.05}
                        label={`第 ${i + 1} 字 ${ch.char} ${DIMENSION_LABEL[dim]}`}
                        disabled={disabled}
                        onChange={(n) => {
                          const next: CharDraft = { ...value, touched: true };
                          (next as unknown as Record<CharDimKey, number | null>)[dim] = n;
                          onChange(i, next);
                        }}
                      />
                    </label>
                  ))}
                </div>

                <Field label="备注" hideLabel>
                  <Input
                    value={value.note ?? ""}
                    placeholder="备注（可选）"
                    aria-label={`第 ${i + 1} 字备注`}
                    disabled={disabled}
                    onChange={(e) =>
                      onChange(i, { ...value, note: e.target.value || null, touched: true })
                    }
                  />
                </Field>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
